import React from 'react'
import {
    ClockCircleOutlined,
    EyeOutlined,
    LogoutOutlined,
    NumberOutlined,
    PlayCircleOutlined,
    SaveOutlined,
    ShoppingCartOutlined,
    WifiOutlined,
} from '@ant-design/icons'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { RecommendedNotificationFilled } from '../../components/icons/recommended-notification'
import { AddStepType } from './types/journey-nodes'

// region Step Categories

export enum StepCategory {
    TIMING = 'timing',
    NOTIFICATIONS = 'notifications',
}

export interface IStepCategoryConfig {
    label: string
    iconClass: string
    order: number
}

export const STEP_CATEGORIES: Record<StepCategory, IStepCategoryConfig> = {
    [StepCategory.TIMING]: {
        label: 'Timing',
        iconClass: 'timing',
        order: 0,
    },
    [StepCategory.NOTIFICATIONS]: {
        label: 'Notifications',
        iconClass: 'notification',
        order: 1,
    },
}

// endregion

// region Helper Functions

/**
 * Get step configuration for a journey node.
 * Handles both ACTION steps (uses configuration.type) and other steps (uses type).
 */
export const getStepConfig = (node: { type: string; configuration?: { type?: string } }) => {
    const stepType = node.type
    const key = stepType === StepType.ACTION ? node.configuration?.type : stepType
    return key ? STEP_CONFIG[key] : undefined
}

// endregion

// region Step Configuration

// System steps (TRIGGER, EXIT) - minimal config
interface ISystemStepConfig {
    kind: 'system'
    nodeLabel: string
    icon: React.ReactNode
}

// User-addable steps - full config
interface IUserStepConfig {
    kind: 'user'
    type: AddStepType
    category: StepCategory
    panelLabel: string
    nodeLabel: string
    drawerLabel: string
    description: string
    icon: React.ReactNode
}

// Union type ensures correct properties for each step type
export type IStepConfig = ISystemStepConfig | IUserStepConfig

export const STEP_CONFIG: Record<string, IStepConfig> = {
    // System steps (always present, not user-addable)
    [StepType.TRIGGER]: {
        kind: 'system',
        nodeLabel: 'Campaign Entry',
        icon: <PlayCircleOutlined />,
    },
    [StepType.EXIT]: {
        kind: 'system',
        nodeLabel: 'Exit Campaign',
        icon: <LogoutOutlined />,
    },
    // User-addable steps
    [StepType.DELAY]: {
        kind: 'user',
        type: StepType.DELAY,
        category: StepCategory.TIMING,
        panelLabel: 'Wait',
        nodeLabel: 'Wait',
        drawerLabel: 'Wait Step',
        description: 'Pause for a set amount of time before continuing the campaign.',
        icon: <ClockCircleOutlined />,
    },
    [JourneyActionType.SEND_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        panelLabel: 'Custom Notification',
        nodeLabel: 'Send Custom Notification',
        drawerLabel: 'Custom Notification',
        description: 'Create and send a customized push notification.',
        icon: <WifiOutlined />,
    },
    [JourneyActionType.SEND_CART_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_CART_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        panelLabel: 'Abandoned Cart',
        nodeLabel: 'Send Abandoned Cart',
        drawerLabel: 'Abandoned Cart Notification',
        description: "Send a reminder about an unpurchased item left in the user's cart.",
        icon: <ShoppingCartOutlined />,
    },
    [JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        panelLabel: 'Saved Item Reminder',
        nodeLabel: 'Send Saved Item Reminder',
        drawerLabel: 'Saved Item Notification',
        description: 'Send a reminder for an item the user saved.',
        icon: <SaveOutlined />,
    },
    [JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        panelLabel: 'Recently Viewed Item',
        nodeLabel: 'Send Recently Viewed Item',
        drawerLabel: 'Recently Viewed Item Notification',
        description: "Send a follow-up for an item the user viewed but didn't purchase.",
        icon: <EyeOutlined />,
    },
    [JourneyActionType.SEND_RANKED_ITEM_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_RANKED_ITEM_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        panelLabel: 'Trending Item',
        nodeLabel: 'Send Trending Item',
        drawerLabel: 'Trending Item Notification',
        description: 'Send the top-performing item based on a configurable activity and time range.',
        icon: <NumberOutlined />,
    },
    [JourneyActionType.SEND_RECOMMENDED_NOTIFICATION]: {
        kind: 'user',
        type: JourneyActionType.SEND_RECOMMENDED_NOTIFICATION,
        category: StepCategory.NOTIFICATIONS,
        panelLabel: 'Personalized Recommendation',
        nodeLabel: 'Send Personalized Recommendation',
        drawerLabel: 'Personalized Recommendation',
        description: 'Send a personalized item selected for the user.',
        icon: <RecommendedNotificationFilled fill="white" className="p-icon-recommended-notification" />,
    },
}

// endregion
